import { useCallback, useEffect, useMemo, useState } from 'react'

import { TrainerDTO } from '@campgladiator/cg-common.types.types'
import {
  CommunicationPreferencesPayload,
  defaultCommunicationPreferences,
  getCommunicationPreferences,
  contactPreferencesToPayload,
  updateCommunicationPreferences,
} from 'services/api/communications'
import { getUserSubscriptions } from 'services/api/subscriptions'
import { getTrainerById } from 'services/api/trainer'
import { MessagingPreferencesDTO } from 'types/communication'

const inactiveStatuses = ['CANCELED', 'INACTIVE', 'SUSPENDED']

type UseWithCommunicationPreferencesArgs = {
  userId?: string
}

const useWithCommunicationPreferences = ({
  userId,
}: UseWithCommunicationPreferencesArgs) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [error, setError] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')

  const [trainer, setTrainer] = useState<TrainerDTO | null>(null)

  const [preferences, setPreferences] =
    useState<CommunicationPreferencesPayload>(defaultCommunicationPreferences())

  const [savedPreferences, setSavedPreferences] =
    useState<CommunicationPreferencesPayload>(defaultCommunicationPreferences())

  const [contactPreferences, setContactPreferences] =
    useState<MessagingPreferencesDTO | null>(null)

  const clearErrors = useCallback(() => {
    setError('')
    setSuccessMessage('')
  }, [])

  const fetchCommunicationPreferences = useCallback(async () => {
    if (!userId) return

    try {
      setIsLoading(true)

      const [preferencesResponse, subscriptions] = await Promise.all([
        getCommunicationPreferences(userId),
        getUserSubscriptions(userId, 0, 1000),
      ])

      setContactPreferences(preferencesResponse)

      const mappedPreferences = contactPreferencesToPayload(preferencesResponse)

      setPreferences(mappedPreferences)
      setSavedPreferences(mappedPreferences)

      const activeBootcampSubscriptions = subscriptions
        .filter(
          ({ productType, status }) =>
            productType === 'BOOTCAMP' &&
            !inactiveStatuses.includes(status ?? ''),
        )
        .sort((a, b) => {
          const dateA = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0

          const dateB = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0

          return dateB - dateA
        })

      const servicingTrainerId =
        activeBootcampSubscriptions[0]?.servicingTrainer

      if (!servicingTrainerId) {
        setTrainer(null)
        return
      }

      const trainerResponse = await getTrainerById(servicingTrainerId)

      setTrainer(trainerResponse)
    } catch (err: any) {
      setError(err?.message || 'Failed to load communication preferences.')
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchCommunicationPreferences()
  }, [fetchCommunicationPreferences])

  const setField = useCallback(
    (key: keyof CommunicationPreferencesPayload) => (value: boolean) => {
      setPreferences((prev) => {
        if (prev[key] === value) return prev

        return {
          ...prev,
          [key]: value,
        }
      })
    },
    [],
  )

  const setMarketingSmsOptIn = useMemo(
    () => setField('marketingSmsOptIn'),
    [setField],
  )

  const setTransactionalSmsOptIn = useMemo(
    () => setField('transactionalSmsOptIn'),
    [setField],
  )

  const setYourTrainerMessagesOptIn = useMemo(
    () => setField('yourTrainerMessagesOptIn'),
    [setField],
  )

  const setAllTrainersMessagesOptIn = useMemo(
    () => setField('allTrainersMessagesOptIn'),
    [setField],
  )

  const handleCancel = useCallback(() => {
    setPreferences(savedPreferences)
  }, [savedPreferences])

  const handleSave = useCallback(async () => {
    if (!contactPreferences?.id) return

    try {
      setIsSaving(true)

      await updateCommunicationPreferences({
        contactId: contactPreferences.id,
        trainerServiceId: contactPreferences?.trainerServiceId ?? trainer?.id,
        currentPreferences: preferences,
        savedPreferences,
      })

      setSavedPreferences(preferences)

      setSuccessMessage('Communication preferences updated successfully.')
    } catch (err: any) {
      setError(err?.message || 'Failed to update communication preferences.')
    } finally {
      setIsSaving(false)
    }
  }, [contactPreferences, trainer, preferences, savedPreferences])

  return {
    error,
    successMessage,
    clearErrors,

    isLoading,
    isSaving,

    trainer,

    marketingSmsOptIn: preferences.marketingSmsOptIn,

    transactionalSmsOptIn: preferences.transactionalSmsOptIn,

    yourTrainerMessagesOptIn: preferences.yourTrainerMessagesOptIn,

    allTrainersMessagesOptIn: preferences.allTrainersMessagesOptIn,

    setMarketingSmsOptIn,
    setTransactionalSmsOptIn,
    setYourTrainerMessagesOptIn,
    setAllTrainersMessagesOptIn,

    handleCancel,
    handleSave,
  }
}

export default useWithCommunicationPreferences
